import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram, Youtube } from 'lucide-react';
import { ImagePaths } from '@/lib/imageUrls';

// Custom icons for TikTok, Bluesky, and WhatsApp
const TikTokIcon = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const BlueskyIcon = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
     <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565-.139 2.038.07 3.711.487 5.722c.413 1.996 2.098 8.663 4.382 11.233.722.812 3.193 2.502 4.197 1.134.195-.266.702-1.343.702-1.343s.508 1.077.702 1.343c1.004 1.368 3.475-.322 4.197-1.134 2.284-2.57 3.969-9.237 4.382-11.233.417-2.011.626-3.684-.415-4.157-.659-.299-1.664-.621-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z"/>
  </svg>
);

const WhatsAppIcon = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);


const Footer = React.memo(() => {
  return (
    <footer className="bg-[#280659] text-white w-full relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div>
            <img
              src={ImagePaths.LOGO_FOOTER}
              alt="Educ4te Logo"
              className="h-16 mb-6 object-contain"
              width="64"
              height="64"
              loading="lazy"
              decoding="async"
              onError={(e) => {
                e.currentTarget.src = 'https://cdn.educ4te.com/logos/white_lion_on_purple.webp';
                e.currentTarget.onerror = null;
              }}
            />
            <p className="text-gray-300 mb-6">
              Empowering organisations with world-class cybersecurity training and comprehensive security assessments.
            </p>
            {/* Updated Grid for 8 icons */}
            <div className="grid grid-cols-4 gap-3">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#F54952] rounded-full flex items-center justify-center transition-colors" aria-label="Follow us on LinkedIn">
                <Linkedin size={20} />
              </a>
              <a href="https://twitter.com/educ4te__" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#F54952] rounded-full flex items-center justify-center transition-colors" aria-label="Follow us on Twitter">
                <Twitter size={20} />
              </a>
              <a href="https://facebook.com/educ4te__" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#F54952] rounded-full flex items-center justify-center transition-colors" aria-label="Follow us on Facebook">
                <Facebook size={20} />
              </a>
              <a href="https://instagram.com/educ4te__" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#F54952] rounded-full flex items-center justify-center transition-colors" aria-label="Follow us on Instagram">
                <Instagram size={20} />
              </a>
              <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#F54952] rounded-full flex items-center justify-center transition-colors" aria-label="Follow us on TikTok">
                <TikTokIcon size={20} />
              </a>
               <a href="https://bsky.app/profile/educ4te.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#F54952] rounded-full flex items-center justify-center transition-colors" aria-label="Follow us on Bluesky">
                <BlueskyIcon size={20} />
              </a>
              <a href="https://www.youtube.com/@educ4te" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#F54952] rounded-full flex items-center justify-center transition-colors" aria-label="Subscribe to our YouTube channel">
                <Youtube size={20} />
              </a>
              <a href="https://wa.me/+61482833614" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#F54952] rounded-full flex items-center justify-center transition-colors" aria-label="Message us on WhatsApp">
                <WhatsAppIcon size={20} />
              </a>
            </div>
          </div>

          <div>
            <span className="text-lg font-bold mb-4 block">Services</span>
            <nav className="flex flex-col space-y-2">
              <Link to="/training" className="text-gray-300 hover:text-[#F54952] transition-colors w-fit py-1">Training Programs</Link>
              <Link to="/training" className="text-gray-300 hover:text-[#F54952] transition-colors w-fit py-1">Certifications</Link>
              <Link to="/assessments" className="text-gray-300 hover:text-[#F54952] transition-colors w-fit py-1">Security Assessments</Link>
              <Link to="/services" className="text-gray-300 hover:text-[#F54952] transition-colors w-fit py-1">Compliance Support</Link>
            </nav>
          </div>

          <div>
            <span className="text-lg font-bold mb-4 block">Company</span>
            <nav className="flex flex-col space-y-2">
              <Link to="/about" className="text-gray-300 hover:text-[#F54952] transition-colors w-fit py-1">About Us</Link>
              <Link to="/about" className="text-gray-300 hover:text-[#F54952] transition-colors w-fit py-1">Our Team</Link>
              <Link to="/about" className="text-gray-300 hover:text-[#F54952] transition-colors w-fit py-1">Careers</Link>
              <Link to="/blog" className="text-gray-300 hover:text-[#F54952] transition-colors w-fit py-1">Blog</Link>
              <Link to="/contact" className="text-gray-300 hover:text-[#F54952] transition-colors w-fit py-1">Contact Us</Link>
            </nav>
          </div>

          <div>
            <span className="text-lg font-bold mb-4 block">Contact</span>
            <div className="space-y-3 text-gray-300">
              <div className="flex items-start gap-3">
                <Mail size={20} className="shrink-0 mt-1 text-[#F54952]" />
                <span>info@educ4te.com</span>
              </div>
              <div className="flex items-start gap-3">
                <Phone size={20} className="shrink-0 mt-1 text-[#F54952]" />
                <span>+61 482 833 614</span>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={20} className="shrink-0 mt-1 text-[#F54952]" />
                <span>Brisbane | Canberra<br />Australia</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © 2025 Educ4te. All rights reserved.
            </p>
            <nav className="flex gap-6 text-sm">
              <Link to="/privacy-policy" className="text-gray-300 hover:text-[#F54952] transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="text-gray-300 hover:text-[#F54952] transition-colors">Terms of Service</Link>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = 'Footer';
export default Footer;